import moment from "moment";
import { ApiMediaURL } from "../../../setting/GlobleVariable";

const DEFAULT_HERO =
  "https://images.unsplash.com/photo-1449748040579-354c191a7934?w=1200&q=80";

export function transformUserCreatedTickets(eventData) {
  if (!eventData?.userTickets?.length) return [];
  return eventData.userTickets.map((ticket, index) => {
    const totalQuantity = parseInt(ticket.quantity, 10) || 0;
    const soldCount = ticket.sold || 0;
    const remainingCount =
      ticket.remaining !== undefined
        ? ticket.remaining
        : totalQuantity - soldCount;

    return {
      id: ticket.id || Date.now() + index,
      tgID: ticket.id || Date.now() + index,
      name: ticket.ticketName,
      price: parseFloat(ticket.price),
      retail_price: parseFloat(ticket.price),
      tgPrice: parseFloat(ticket.price),
      section: ticket.ticketName,
      tgUserSec: ticket.ticketName,
      available_count: remainingCount,
      products_count: totalQuantity,
      tgQty: remainingCount,
      available_quantity: remainingCount,
      totalQuantity,
      soldCount,
      remainingCount,
      splits: Array.from({ length: remainingCount }, (_, i) => i + 1),
      eticket: ticket.serviceFee === "pass-to-buyer",
      face_value: parseFloat(ticket.price),
      row: "GA",
      tgUserRow: "GA",
      seat_numbers: null,
      instant_delivery: true,
      ticket_type: "eticket",
      tgCType: ticket.description || ticket.ticketName,
      service_fee: 0,
      sectionStyle: { level: { color: "#CD292E" } },
    };
  });
}

export function normalizeTicketRows(ticketData, isUserCreated) {
  if (!ticketData?.length) return [];

  return ticketData
    .map((item, index) => {
      const id = String(item.id ?? item.tgID ?? index);
      const price = parseFloat(
        item.retail_price ?? item.price ?? item.tgPrice ?? 0
      );
      const available = isUserCreated
        ? item.remainingCount ?? item.available_count ?? item.tgQty ?? 0
        : item.available_quantity ?? item.available_count ?? item.tgQty ?? 0;
      const splits = Array.isArray(item.splits)
        ? item.splits.map(Number).filter((n) => n > 0)
        : [];
      const maxQty = splits.length
        ? Math.max(...splits)
        : Math.min(10, available || 10);

      const sectionLabel =
        item.tgUserSec ||
        item.name ||
        (typeof item.section === "string" ? item.section : "General Admission");

      const rowLabel = item.row || item.tgUserRow;
      const description =
        item.tgCType ||
        (rowLabel ? `Row ${rowLabel}` : "Verified listing on InstaPass");

      return {
        id,
        raw: item,
        name: sectionLabel,
        description,
        price,
        available,
        maxQty,
        splits,
      };
    })
    .sort((a, b) => a.price - b.price);
}

export function buildLineup(event) {
  if (!event) return [];
  const fromPerformances =
    event.performances
      ?.map((p) => p?.performer?.name || p?.name)
      .filter(Boolean) || [];
  if (fromPerformances.length) return fromPerformances;
  if (Array.isArray(event.lineup) && event.lineup.length) return event.lineup;
  if (event.artist) return [event.artist];
  return [];
}

export function computeSoldPercent(event, ticketRows) {
  if (event?.totalTickets && event?.soldTickets) {
    return Math.round((event.soldTickets / event.totalTickets) * 100);
  }

  let total = 0;
  let available = 0;
  ticketRows.forEach((row) => {
    const products = row.raw?.products_count ?? row.available + (row.raw?.soldCount || 0);
    total += products || row.available;
    available += row.available;
  });

  if (total > 0) {
    return Math.min(100, Math.round(((total - available) / total) * 100));
  }

  if (event?.available_count === 0) return 100;
  return 0;
}

export function resolveCheckoutEventImage(event, heroImage) {
  if (heroImage) return heroImage;
  if (!event) return DEFAULT_HERO;
  if (event._isUserCreated && event.image) {
    return `${ApiMediaURL}/${event.image}`;
  }
  if (typeof event.image === "string") {
    if (event.image.startsWith("http")) return event.image;
    return `${ApiMediaURL}/${event.image}`;
  }
  if (event.configuration?.primary_image) return event.configuration.primary_image;
  if (event.configuration?.image) return event.configuration.image;
  return DEFAULT_HERO;
}

export function mapEventForDisplay(event, heroImage, isUserCreated) {
  if (!event) return null;

  const occurs = event.occurs_at_local || event.occurs_at;
  const m = occurs ? moment(occurs) : null;
  const venueName = event.venue?.name || "";
  const venueLoc = event.venue?.location || "";
  const city = event.venue?.city || event.venue?.state || "";
  const locationLine = [venueName || venueLoc, city].filter(Boolean).join(", ");

  const image = resolveCheckoutEventImage(event, heroImage);

  const organizerName =
    event.organizerName ||
    event.createdBy?.name ||
    event.organizer?.name ||
    "InstaPass";
  const organizerAvatar =
    event.organizer?.avatar ||
    String(organizerName)
      .split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  return {
    id: event.id || event._id,
    title: event.name || "Event",
    category: event.category?.name || event.category || "",
    date: m?.isValid() ? m.format("ddd, MMM D · h:mm A") : "Date TBA",
    time: m?.isValid() ? m.format("h:mm A") : "",
    venue: venueName || venueLoc || "Venue TBA",
    city: city || "",
    locationLine,
    image,
    description:
      event.description ||
      event.paragraph ||
      `Join us for ${event.name || "this event"}. Secure your tickets on InstaPass with instant digital delivery.`,
    lineup: buildLineup(event),
    organizer: { name: organizerName, avatar: organizerAvatar },
    badge: event.badge || (event.featured ? "HOT" : null),
    available_count: event.available_count,
  };
}

export function buildCheckoutPayload(raw, qty) {
  return {
    id: raw.id ?? raw.tgID,
    qty,
    retail_price: raw.retail_price ?? raw.tgPrice ?? raw.price,
    format: raw.format,
    section: raw.section,
    row: raw.row,
    seat_numbers: raw.seat_numbers,
    available_quantity: raw.available_quantity,
    available_count: raw.available_count,
    tgQty: raw.tgQty,
    tgPrice: raw.tgPrice,
    tgUserSec: raw.tgUserSec,
    tgUserRow: raw.tgUserRow,
    tgCType: raw.tgCType,
    tgID: raw.tgID,
    service_fee: raw.service_fee,
    eticket: raw.eticket,
    face_value: raw.face_value,
    instant_delivery: raw.instant_delivery,
    ticket_type: raw.ticket_type,
  };
}

export function quantityAllowed(qty, row, isUserCreated) {
  if (qty < 1) return false;
  if (qty > row.maxQty || qty > row.available) return false;
  if (!isUserCreated && row.splits?.length && !row.splits.includes(qty)) {
    return false;
  }
  return true;
}
